import { useCallback, useEffect, useRef, useState } from 'react';
import { apiApp, apiAppCMS, apiAppSSO } from 'api/request';
import urlApp from 'api/urlApp';
import { REACT_APP_SITE_KEY } from 'utils/env';
import { setStorageData, STORAGE_KEY } from 'utils/storage';
import {
  Article,
  ArticleData,
  ArticleApiResponse,
  ArticleFormData,
  ArticleListResponse,
} from 'types/article';

export const ARTICLE_SITE_KEY = REACT_APP_SITE_KEY || 'ghtm_kh';

// ── Transformers ──────────────────────────────────────────────────────────────

/** createdAt/updatedAt từ API có thể là số (Unix ms) hoặc ISO string → chuẩn hoá thành ISO string */
function toISOString(value: string | number | undefined): string {
  if (!value) return new Date().toISOString();
  // Nếu là số → Unix timestamp ms
  if (typeof value === 'number') return new Date(value).toISOString();
  // Nếu là chuỗi chỉ có số → parse as number
  if (/^\d+$/.test(value)) return new Date(parseInt(value, 10)).toISOString();
  return value;
}

export function toArticle(data: ArticleData): Article {
  return {
    id: data.id.toString(),
    title: data.title,
    slug: data.slug,
    content: data.content,
    thumbnail: data.thumbnail || '',
    excerpt: data.excerpt || '',
    author: data.author || 'Admin',
    published: data.published,
    articleType: data.articleType as Article['articleType'],
    createdAt: toISOString(data.createdAt as unknown as number | string),
    updatedAt: toISOString(data.updatedAt as unknown as number | string),
    siteKey: data.siteKey,
    metaTitle: data.metaTitle,
    metaDescription: data.metaDescription,
    focusKeyword: data.focusKeyword,
    canonicalUrl: data.canonicalUrl,
    ogTitle: data.ogTitle,
    ogDescription: data.ogDescription,
    ogImage: data.ogImage,
    noindex: data.noindex,
  };
}

export function sortByNewest(articles: Article[]): Article[] {
  const getTime = (value?: string) => {
    if (!value) return 0;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? 0 : time;
  };

  const getId = (value: string) => {
    const id = parseInt(value, 10);
    return Number.isNaN(id) ? 0 : id;
  };

  return [...articles].sort((a, b) => {
    const createdAtDiff = getTime(b.createdAt) - getTime(a.createdAt);
    if (createdAtDiff !== 0) return createdAtDiff;

    const updatedAtDiff = getTime(b.updatedAt) - getTime(a.updatedAt);
    if (updatedAtDiff !== 0) return updatedAtDiff;

    return getId(b.id) - getId(a.id);
  });
}

// ── Generic fetch state ───────────────────────────────────────────────────────

interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function useFetchOnce<T>(fetcher: () => Promise<T>, deps: unknown[] = []) {
  const [state, setState] = useState<FetchState<T>>({
    data: null,
    loading: true,
    error: null,
  });
  const abortRef = useRef<AbortController | null>(null);

  const fetch = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await fetcher();
      setState({ data, loading: false, error: null });
    } catch (err: unknown) {
      // Ignore axios cancel errors
      const errName = (err as Error)?.name;
      if (errName === 'CanceledError' || errName === 'AbortError' || errName === 'Cancel') return;
      setState({ data: null, loading: false, error: (err as Error).message ?? 'Lỗi không xác định' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    fetch();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetch]);

  return state;
}

// ── Public hooks ──────────────────────────────────────────────────────────────

/**
 * Fetch danh sách bài viết đã published.
 * @param articleType - 'MKL' | 'INDUSTRY' | undefined (tất cả)
 */
export function usePublishedArticles(articleType?: string) {
  return useFetchOnce<Article[]>(async () => {
    const params: Record<string, string> = { sort: 'id', order: 'desc', siteKey: ARTICLE_SITE_KEY };
    if (articleType) params.articleType = articleType;

    const { data } = await apiAppCMS.get<ArticleApiResponse<ArticleData[]>>(
      urlApp.articleListPublished,
      { params }
    );
    return sortByNewest((data.data ?? []).map(toArticle));
  }, [articleType]);
}

/**
 * Fetch bài viết theo slug.
 */
export function useArticleBySlug(slug: string | undefined) {
  return useFetchOnce<Article>(async () => {
    const { data } = await apiAppCMS.get<ArticleApiResponse<ArticleData>>(
      `${urlApp.articleBySlug}/${slug}`,
      { params: { siteKey: ARTICLE_SITE_KEY } }
    );
    return toArticle(data.data);
  }, [slug]);
}

export interface AdminUser {
  id: number;
  username: string;
  fullName: string;
  postCode?: string;
  postId?: number;
}

interface LoginResponse {
  data: {
    token: string;
    data: {
      id: number;
      username: string;
      full_name?: string;
      fullName?: string;
      postCode?: string;
      postId?: number;
    };
  };
}

export async function adminLogin(username: string, password: string): Promise<AdminUser> {
  const { data } = await apiAppSSO.post<LoginResponse>(
    '/sso/cmsLogin',
    { username, password },
    {
      headers: { Authorization: 'autogenerate' },
      params: { version: '1.0' },
    }
  );
  const token = data.data.token;
  const user = data.data.data;

  setStorageData(STORAGE_KEY.ACCESS_TOKEN, token);
  setStorageData(STORAGE_KEY.USER_INFO, user);

  return {
    id: user.id,
    username: user.username,
    fullName: user.full_name || user.fullName || user.username,
    postCode: user.postCode,
    postId: user.postId,
  };
}

function pickArticlePayload(data: Partial<ArticleFormData>) {
  return {
    ...data,
    siteKey: ARTICLE_SITE_KEY,
  };
}

export function useAdminArticles() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiApp.get<ArticleApiResponse<ArticleListResponse>>(
        '/article/list',
        { params: { token: 1, limit: 100, sort: 'id', order: 'desc', siteKey: ARTICLE_SITE_KEY, version: '1.0' } }
      );
      setArticles(sortByNewest((data.data.data ?? []).map(toArticle)));
    } catch (err: unknown) {
      setError((err as Error).message || 'Không tải được bài viết');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const getById = useCallback(async (id: string) => {
    const { data } = await apiApp.get<ArticleApiResponse<ArticleData>>(
      `/article/${id}`,
      { params: { version: '1.0' } }
    );
    return toArticle(data.data);
  }, []);

  const createArticle = useCallback(async (payload: ArticleFormData) => {
    setSaving(true);
    try {
      const { data } = await apiApp.post<ArticleApiResponse<ArticleData>>(
        '/article',
        pickArticlePayload(payload),
        { params: { version: '1.0' } }
      );
      const article = toArticle(data.data);
      setArticles(prev => sortByNewest([article, ...prev]));
      return article;
    } finally {
      setSaving(false);
    }
  }, []);

  const updateArticle = useCallback(async (id: string, payload: Partial<ArticleFormData>) => {
    setSaving(true);
    try {
      const { data } = await apiApp.put<ArticleApiResponse<ArticleData>>(
        `/article/${id}`,
        pickArticlePayload(payload),
        { params: { version: '1.0' } }
      );
      const article = toArticle(data.data);
      setArticles(prev => sortByNewest(prev.map(item => (item.id === id ? article : item))));
      return article;
    } finally {
      setSaving(false);
    }
  }, []);

  const deleteArticle = useCallback(async (id: string) => {
    await apiApp.delete<ArticleApiResponse<boolean>>(`/article/${id}`, { params: { version: '1.0' } });
    setArticles(prev => prev.filter(item => item.id !== id));
  }, []);

  return {
    articles,
    loading,
    saving,
    error,
    loadArticles,
    getById,
    createArticle,
    updateArticle,
    deleteArticle,
  };
}
